import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";

import { Button, Label } from "semantic-ui-react";
import TemplateTable from "../../components/TemplateTable";
import { ITableAppRender } from "../../components/TableApp";


import OrderRepository, { IOrderListModel } from "../../Repositories/OrderRepository";
import api from "../../services/api";
import { ISearch } from "../../components/Search";

import errors from "../../Utils/ExtractErrors";
import { ISearchResponse } from "../../interfaces";
import { IFields } from "../../components/Search/FilterBuilder";
import { GetUser } from "../../services/auth";
import ReciboPedido from "../Orders/ReciboPedido";


const Orders: React.FC = () => {

    const [list, setList] = useState<IOrderListModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    const { role } = GetUser();

    const onChangeRecords = (value: number) => {
        refRecordsByPage.current = value;
        load();
    }

    const onPageChange = (pageNumber: string | number | undefined) => {
        refPageNumber.current = pageNumber;
        load();
    }

    const setStatus = (status: number) => {
        if (status)
            return <Label ribbon icon="check" color="pink" content="ATI" />
        else
            return <Label ribbon icon="times" color="grey" content="INA" />
    }

    const setLiberacao = (liberacao: number) => {
        switch (liberacao) {
            case 0:
                return <Label color="red" align="center" content="Faltando foto" />
            case 1:
                return <Label color="brown" align="center" content="Aguardando liberação" />
            case 2:
                return <Label color="green" align="center" content="Liberado" />
        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (x: IOrderListModel) => setStatus(x.status),
            textAlign: "left",
            width: 1
        },
        {
            header: "Recibo",
            content: (x: IOrderListModel) => setLiberacao(x.liberacao),
            textAlign: "left",
            width: 1
        },
        {
            header: "Código",
            content: (x: IOrderListModel) => x.codigo,
            textAlign: "center",
            width: 1
        },
        {
            header: "G. Regional",
            content: (x: IOrderListModel) => x.gerenteRegional.split(" ")[0],
            width: 3
        },
        {
            header: "G. Setor",
            content: (x: IOrderListModel) => x.gerenteSetor,
            width: 3
        },
        {
            header: "Representante",
            content: (x: IOrderListModel) => x.representante,
            width: 4
        },
        {
            header: "Acerto",
            content: (x: IOrderListModel) => format(new Date(x.acerto), "dd/MM/yyyy"),
            textAlign: "center",
            width: 1
        },
    ];


    const handlePrint = (id: string) => {
        var a = document.createElement("a");
        a.href = `${api.defaults.baseURL}/pedido/${id}/print`;
        a.setAttribute("target", "_blank");
        a.click();
    }

    const PrintButton = (id: string) => (
        <Button
            icon="print"
            color="blue"
            onClick={() => handlePrint(id)}
        />
    );

    const EditButton = (id: string) => (
        <Button
            as={Link}
            icon="pencil"
            color="pink"
            to={`/updateorder/${id}`}
        />
    );

    const load = useCallback(async () => {

        setLoading(true);
        try {
            let response: ISearchResponse<IOrderListModel>;

            if (refSearch.current.type === "simples") {
                response = await OrderRepository.List({
                    search: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            } else {
                response = await OrderRepository.List({
                    filter: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setList(response.list);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, []);

    const onSearch = ({ searchString, type }: ISearch) => {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;

        load();
    }

    useEffect(() => { load() }, [load]);

    const fields: IFields[] = [
        { label: "G. Regional", name: "GerenteRegional.Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "G. Setor", name: "GerenteSetor.Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "Representante", name: "Representante.Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "Número", name: "Codigo", type: "number", roles: ["Admin", "Operador"] },
        { label: "Acerto", name: "Acerto", type: "date", roles: ["Admin", "Operador"] },
        {
            label: "Status",
            name: "Status",
            type: "enum",
            values: [
                { key: "INA", text: "INA", value: 0, label: { icon: "times", color: "grey", content: "INA" } },
                { key: "ATI", text: "ATI", value: 1, label: { icon: "check", color: "pink", content: "ATI" } },
            ],
            roles: ["Admin", "Operador"]
        },
    ];

    return (
        <TemplateTable
            title="Pedidos"
            icon="list alternate"
            insert={<Button
                disabled={(role !== "Admin" && role !== "Operador")}                
                icon="add"
                color="pink"
                content="Novo"
                labelPosition="left"
                floated="right"
                as={Link}
                to="/createorder"
            />}
            tableList={list}
            tableRender={tableRender}
            tableActions={(data: IOrderListModel, index: number) =>
                (role === "Admin" || role === "Operador") ? [
                    EditButton(data.id || ""),
                    PrintButton(data.id || ""),
                    <ReciboPedido key={`${data.id}`} pedidoId={data.id || ""} />
                ] : [
                    <ReciboPedido key={`${data.id}`} pedidoId={data.id || ""} />,
                ]
            }
            searchPlaceHolder="Código/G. Regional/G. Setor/Representante/Acerto"
            onSearch={onSearch}
            filterFields={fields}
            tableLoading={loading}
            tableOnChangeRecords={onChangeRecords}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
        />
    );
};

export default Orders;
